// Repay Loan – PanMobile-like UI with PAN-only input
// Calls POST /user/send-otp-for-paynow on "Continue" button
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { squidloansUserAPI } from "../services/api";
import FlowFooter from "../components/FlowFooter";
import RepayInfoPopup from "../components/RepayInfoPopup/RepayInfoPopup";
import Toast from "../components/Toast";

function RepayLoan() {
  const navigate = useNavigate();
  const [pan, setPan] = useState("");
  const [errors, setErrors] = useState({ pan: "" });
  const [agreed, setAgreed] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "warning" });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };
  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const validatePAN = (p) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(p);

  const formatPAN = (val) => {
    const cleaned = val.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 10);
    if (cleaned.length <= 5) return cleaned;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 9)} ${cleaned.slice(9)}`;
  };

  const handlePANKeyDown = (e) => {
    if (
      [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      ((e.keyCode === 65 || e.keyCode === 67 || e.keyCode === 86 || e.keyCode === 88) && e.ctrlKey)
    )
      return;

    const input = e.target;
    const currentValue = pan.replace(/[^A-Z0-9]/gi, "").toUpperCase();
    const cursorPos = input.selectionStart;
    const valueBeforeCursor = currentValue.slice(0, cursorPos);
    const position = valueBeforeCursor.length;

    let allowedPattern;
    if (position < 5) allowedPattern = /^[A-Z]$/i;
    else if (position < 9) allowedPattern = /^[0-9]$/;
    else if (position === 9) allowedPattern = /^[A-Z]$/i;
    else {
      e.preventDefault();
      return;
    }

    if (!allowedPattern.test(e.key)) e.preventDefault();
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    const cleaned = value.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 10);

    let formatted = "";
    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      if (i < 5 && /[A-Z]/.test(char)) formatted += char;
      else if (i >= 5 && i < 9 && /[0-9]/.test(char)) formatted += char;
      else if (i === 9 && /[A-Z]/.test(char)) formatted += char;
    }

    setPan(formatted);
    setErrors((prev) => ({ ...prev, pan: "" }));
    setApiError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ pan: "" });
    setShowConsentError(false);
    setApiError("");

    const PAN = pan.trim().toUpperCase();
    if (!PAN) {
      setErrors((prev) => ({ ...prev, pan: "PAN is required" }));
      return;
    }
    if (!validatePAN(PAN)) {
      setErrors((prev) => ({ ...prev, pan: "Invalid PAN format" }));
      return;
    }
    if (!agreed) {
      setShowConsentError(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await squidloansUserAPI.sendOtpForPaynow(PAN);
      setApiError("");
      if (response?.success === true) {
        const phone_number = response?.data?.mobile;
        const message = response?.message;
        const stateData = { PAN, ...(phone_number && { phone_number }), ...(message && { message }) };
        navigate("/verify-repay-otp", { state: stateData });
      } else {
        showToast(response?.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to send OTP. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
    setAgreed(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <RepayInfoPopup show={showInfoModal} onClose={handleCloseInfoModal} />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-6xl w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-[#1E3A8A]">
              Repay your <span className="text-[#FF6B35]">Snap Paisa</span> loan
            </h1>
            <p className="text-sm sm:text-base text-[#64748B] max-w-2xl mx-auto">
              Enter your PAN to continue
            </p>
          </div>

          <div className="flex justify-center px-2 sm:px-0">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-7 lg:p-8 shadow-2xl shadow-[#FF6B35]/10 w-full max-w-xl">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 mt-2 sm:mt-3 md:mt-4">
                <div className="space-y-2 sm:space-y-4">
                  <label
                    htmlFor="pan"
                    className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block"
                  >
                    PAN Number <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="text"
                    id="pan"
                    name="pan"
                    value={formatPAN(pan)}
                    onChange={handleInputChange}
                    onKeyDown={handlePANKeyDown}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = (e.clipboardData || window.clipboardData).getData("text");
                      const cleaned = pasted.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 10);
                      let formatted = "";
                      for (let i = 0; i < cleaned.length; i++) {
                        const char = cleaned[i];
                        if (i < 5 && /[A-Z]/.test(char)) formatted += char;
                        else if (i >= 5 && i < 9 && /[0-9]/.test(char)) formatted += char;
                        else if (i === 9 && /[A-Z]/.test(char)) formatted += char;
                      }
                      setPan(formatted);
                      setErrors((prev) => ({ ...prev, pan: "" }));
                      setApiError("");
                    }}
                    placeholder="ABCDE 1234 F"
                    maxLength={12}
                    autoComplete="off"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${errors.pan ? "border-red-500" : "border-[#E2E8F0] focus:border-[#FF6B35]"
                      } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 transition-all uppercase tracking-wider font-semibold`}
                  />
                  {errors.pan && (
                    <p className="text-xs text-red-500 mt-1 sm:mt-3">{errors.pan}</p>
                  )}
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl sm:rounded-2xl p-3 sm:p-4 space-y-2 sm:space-y-3">
                  <label className="flex items-start gap-3 sm:gap-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      readOnly
                      onClick={(e) => {
                        e.preventDefault();
                        setShowInfoModal(true);
                        setShowConsentError(false);
                      }}
                      className="mt-1 h-4 w-4 sm:h-5 sm:w-5 rounded border-[#CBD5E1] accent-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/40 focus:ring-offset-0 cursor-pointer flex-shrink-0"
                    />
                    <span
                      className="text-xs sm:text-sm text-[#1E293B] flex-1 leading-relaxed pt-0.5"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowInfoModal(true);
                        setShowConsentError(false);
                      }}
                    >
                      I have read and understood the security alert{" "}
                      <span className="text-[#FF6B35] font-semibold underline">(click to view)</span>
                    </span>
                  </label>
                  {showConsentError && (
                    <p className="text-xs text-red-500 mt-1 sm:mt-2">
                      Please read and acknowledge the security alert
                    </p>
                  )}
                </div>

                {apiError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-red-700">
                    {apiError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Sending OTP...</span>
                    </span>
                  ) : (
                    "Click to continue"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}

export default RepayLoan;
